import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, GlobeIcon } from '@persona-id/design-tokens/icons';
import { capitalize, keyBy } from 'lodash/fp';
import { ReactNode } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import DeviceIcon from 'dashboard/components/DeviceIcon';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Device } from 'dashboard/models/device';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { DeviceAttribute, ModuleDevice } from 'dashboard/modules/Device';
import Placeholder from 'dashboard/modules/Placeholder';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';

interface DeviceModuleProps {
  module: ModuleDevice;
  sessions: InquirySession[];
  devices: Device[] | null;
  isLoading: boolean;
}

const DeviceModule = ({ module, sessions = [], devices, isLoading }: DeviceModuleProps) => {
  // TODO: (melissa) clean this up after migrating device modules to always
  // include an attribute list.
  const DEFAULT_ATTRIBUTES: DeviceAttribute[] = [
    'browser',
    'browser-fingerprint',
    'country-name',
    'device-handoff-method',
    'device-vendor-id',
    'ip-address',
    'is-datacenter',
    'is-isp',
    'is-proxy',
    'is-tor',
    'is-vpn',
    'latitude',
    'longitude',
    'os',
    'region-name',
    'threat-level',
    'type',
  ];

  // If module is not passed in, display the default attributes.
  const attributeList = module?.attributes || DEFAULT_ATTRIBUTES;

  const sessionsToDisplay = Object.values(sessions).filter(
    (session) => session.attributes['started-at'] != null,
  );

  if (isLoading) {
    return <Placeholder title='Device module' loading={true} />;
  }

  if (!sessionsToDisplay || sessionsToDisplay.length === 0) {
    return <Placeholder title='Device module' message='No devices found for this case.' />;
  } else {
    const sessions = sessionsToDisplay.map((session) => {
      const device = keyBy('id', devices)[session.relationships.device.data?.id];
      const isRedacted = session.attributes['redacted-at'] != null;

      return (
        <DeviceDetail
          key={session.id}
          device={device}
          inquirySession={session}
          attributeList={attributeList}
          isRedacted={isRedacted}
        />
      );
    });

    return <div data-test={`module__device--${module.token}`}>{sessions}</div>;
  }
};

interface DeviceDetailProps {
  device: Device;
  inquirySession: InquirySession;
  attributeList: DeviceAttribute[];
  isRedacted: boolean;
}

const DeviceDetail = ({ device, inquirySession, attributeList, isRedacted }: DeviceDetailProps) => {
  const {
    'threat-level': threatLevel,
    'ip-address': ipAddress,
    'country-name': countryName,
    'region-name': regionName,
    latitude,
    longitude,
    'device-handoff-method': deviceHandoffMethod,
    'is-proxy': isProxy,
    'is-tor': isTor,
    'is-datacenter': isDatacenter,
    'ip-isp': ipIsp,
    'is-vpn': isVpn,
    'ip-connection-type': ipConnectionType,
  } = inquirySession.attributes;

  const {
    'browser-full-version': browserFullVersion,
    'browser-name': browserName,
    'device-type': deviceType,
    'device-name': deviceName,
    'os-full-version': osFullVersion,
    'os-name': osName,
    'browser-fingerprint': browserFingerprint,
    'device-vendor-id': deviceVendorId,
  } = device?.attributes || {};

  const threatLevelDisplay: ReactNode = threatLevel != null ? capitalize(threatLevel) : null;

  const ipAddressAttribute = (
    <AttributeItem
      title='IP address'
      value={ipAddress}
      isRedacted={isRedacted}
      placeholder='Unknown'
    />
  );
  const threatRatingAttribute = (
    <AttributeItem title='Threat rating' value={threatLevelDisplay} placeholder='Unknown' />
  );
  const networkCountryAttribute = (
    <AttributeItem
      title='Network country'
      value={countryName}
      isRedacted={isRedacted}
      placeholder='Unknown'
    />
  );
  const networkRegionAttribute = (
    <AttributeItem
      title='Network region'
      value={regionName}
      isRedacted={isRedacted}
      placeholder='Unknown'
    />
  );
  const latitudeAttribute = (
    <AttributeItem
      title='Latitude'
      value={latitude}
      isRedacted={isRedacted}
      placeholder='Unknown'
    />
  );
  const longitudeAttribute = (
    <AttributeItem
      title='Longitude'
      value={longitude}
      isRedacted={isRedacted}
      placeholder='Unknown'
    />
  );
  const torConnectionAttribute = (
    <AttributeItem
      title='Tor connection'
      value={<YesOrNo value={!!isTor} />}
      placeholder='Unknown'
    />
  );
  const proxyConnectionAttribute = (
    <AttributeItem
      title='Public proxy'
      value={<YesOrNo value={!!isProxy} />}
      placeholder='Unknown'
    />
  );
  const datacenterConnectionAttribute = (
    <AttributeItem
      title='Private proxy'
      value={<YesOrNo value={!!isDatacenter} />}
      placeholder='Unknown'
    />
  );
  const serviceProviderAttribute = (
    <AttributeItem title='ISP' value={ipIsp} placeholder='Unknown' />
  );
  const VpnConnectionAttribute = (
    <AttributeItem
      title='VPN'
      key='VPN'
      value={<YesOrNo value={!!isVpn} />}
      placeholder='Unknown'
    />
  );
  const ipConnectionTypeAttribute = (
    <AttributeItem title='Connection Type' value={ipConnectionType} placeholder='Unknown' />
  );

  const typeAttribute = (
    <AttributeItem
      title='Device type'
      value={capitalize([deviceType, deviceName].filter(Boolean).join(', '))}
      placeholder='Unknown'
    />
  );
  const osAttribute = (
    <AttributeItem
      title='Device OS'
      value={[osName, osFullVersion].filter(Boolean).join(' ')}
      placeholder='Unknown'
    />
  );
  const browserAttribute = (
    <AttributeItem
      title='Browser'
      value={[browserName, browserFullVersion].filter(Boolean).join(' ')}
      placeholder='Unknown'
    />
  );
  const browserFingerprintAttribute = (
    <AttributeItem
      title={
        <>
          Browser fingerprint{' '}
          <HelpIcon popoverWidth={300}>
            Unique value computed from a systematic collection of information about a device
          </HelpIcon>{' '}
        </>
      }
      value={browserFingerprint}
      wordBreak='break-word'
      placeholder='Unknown'
    />
  );
  const deviceHandoffMethodAttribute = (
    <AttributeItem title='Device handoff method' value={deviceHandoffMethod} placeholder='N/A' />
  );
  const deviceVendorIdAttribute = (
    <AttributeItem
      title={
        <>
          Device Vendor ID{' '}
          <HelpIcon popoverWidth={300}>
            Unique identifier provided by the SDK, re-identifies the device even when a user
            switches accounts
          </HelpIcon>{' '}
        </>
      }
      key='Device Vendor ID'
      value={deviceVendorId}
      placeholder='Unknown'
    />
  );

  const getNetworkAttributeByType = (attr: DeviceAttribute) => {
    switch (attr) {
      case 'ip-address':
        return ipAddressAttribute;
      case 'threat-level':
        return threatRatingAttribute;
      case 'country-name':
        return networkCountryAttribute;
      case 'region-name':
        return networkRegionAttribute;
      case 'latitude':
        return latitudeAttribute;
      case 'longitude':
        return longitudeAttribute;
      case 'is-tor':
        return torConnectionAttribute;
      case 'is-proxy':
        return proxyConnectionAttribute;
      case 'is-datacenter':
        return datacenterConnectionAttribute;
      case 'is-isp':
        return serviceProviderAttribute;
      case 'is-vpn':
        return VpnConnectionAttribute;
      case 'ip-connection-type':
        return ipConnectionTypeAttribute;
    }
  };

  const getDeviceAttributeByType = (attr: DeviceAttribute) => {
    switch (attr) {
      case 'type':
        return typeAttribute;
      case 'os':
        return osAttribute;
      case 'browser':
        return browserAttribute;
      case 'browser-fingerprint':
        return browserFingerprintAttribute;
      case 'device-handoff-method':
        return deviceHandoffMethodAttribute;
      case 'device-vendor-id':
        return deviceVendorIdAttribute;
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title icon={<GlobeIcon />}>
          {[ipAddress, countryName].filter(Boolean).join(', ')}
        </Card.Title>
        <Spacer height={1} />
        <AttributeGrid>
          {attributeList.map((attribute) => getNetworkAttributeByType(attribute))}
        </AttributeGrid>
        {device && (
          <>
            <Spacer height={1} />
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <Card.Title icon={<DeviceIcon deviceType={deviceType} deviceName={deviceName} />}>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {[capitalize(deviceType), osName].filter(Boolean).join(', ')}
            </Card.Title>
            <Spacer height={1} />
            <AttributeGrid>
              {attributeList.map((attribute) => getDeviceAttributeByType(attribute))}
            </AttributeGrid>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const IconAlertCircleStyled = styled(CircleAlertIcon)`
  margin-left: ${Spacing[2]};
  path,
  rect {
    stroke: ${Color.CoreYellow100};
  }
`;

const YesOrNo = ({ value }: { value: boolean }) =>
  value ? (
    <>
      Yes <IconAlertCircleStyled />{' '}
    </>
  ) : (
    <>No</>
  );

export default DeviceModule;
